/**
 * Settings API service for user preferences.
 */

import { BaseApiService } from './BaseApiService';
import { API_URL } from '@/lib/apiConfig';

export interface UserSettings {
  language?: string;
  appMode?: boolean;
  templateCategories?: unknown[];
}

class SettingsApiService extends BaseApiService {
  constructor() {
    super(API_URL, 'SettingsApi');
  }

  /**
   * Get user settings.
   */
  async getUserSettings(): Promise<UserSettings | null> {
    this.log.debug('Getting user settings');
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/user/settings`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get settings', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get settings');
    }

    const data = await response.json();
    const settings = data.settings;

    if (!settings || Object.keys(settings).length === 0) {
      return null;
    }

    this.log.info('Settings retrieved', { keys: Object.keys(settings) });
    return settings;
  }

  /**
   * Save user settings (merge with existing).
   */
  async saveUserSettings(settings: Partial<UserSettings>): Promise<void> {
    this.log.debug('Saving user settings', { keys: Object.keys(settings) });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/user/settings`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(settings),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to save settings', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to save settings');
    }

    this.log.info('Settings saved', { keys: Object.keys(settings) });
  }

  /**
   * Sync user profile to DynamoDB (called on login).
   * Stores email, plan, and fullName so backend functions can access
   * user attributes when using access token authentication.
   */
  async syncProfile(profile: { email?: string; plan?: string; fullName?: string }): Promise<void> {
    this.log.debug('Syncing user profile', { email: profile.email, plan: profile.plan });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/user/profile`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(profile),
    });

    if (!response.ok) {
      // Profile sync is best-effort — don't throw, just log
      const data = await response.json().catch(() => ({}));
      this.log.warn('Profile sync failed', { error: data.error || `HTTP ${response.status}` });
      return;
    }

    this.log.info('Profile synced');
  }
}

// Export singleton instance
export const settingsApi = new SettingsApiService();
